"use client";

import { useMemo, useRef, useState, type ReactNode } from "react";
import { RotateCcw, SearchX } from "@/components/icons";
import { EmptyState } from "@/components/ui/empty-state";
import type { BreadcrumbItemType } from "@/components/ui/breadcrumbs";
import { ListingEmptyState } from "@/components/listings/listing-empty-state";
import {
  CatalogueHeaderBar,
  CatalogueMapColumn,
  CatalogueMobileMap,
  CatalogueQuoteCard,
  CatalogueResultsHeading,
  CatalogueShell,
  scrollCatalogueToTop,
} from "@/components/listing/browse/catalogue-shell";
import { ListingsClusterMapClient } from "@/components/location/listings-cluster-map-client";
import type { ClusterListing } from "@/components/location/cluster-map-points";
import { clearSearchAndFilters, type BrowserListing } from "./listing-filters";
import { ListingBrowserControls, listingCountLabel } from "./listing-browser-toolbar";
import { ListingPagination } from "./listing-pagination";
import { ListingResultsGrid } from "./listing-results-grid";
import { useListingResults, type ListingResults, type LocationListingSource } from "./use-listing-results";

type MapView = {
  focalCenter?: { latitude: number; longitude: number };
  defaultZoom: number;
  mobileTitle: string;
};

type LocationListingBrowserProps = LocationListingSource & {
  hasMap: boolean;
  productName: string;
  locationName: string;
  emptyMessage: string;
  hasDistance: boolean;
  mapView: MapView;
  title: string;
  summary: string;
  breadcrumbs: BreadcrumbItemType[];
  quoteProductSlug: string;
  seo: ReactNode;
};

type Highlight = { id: string; source: "card" | "map" } | null;

type Located = BrowserListing & { cityLatitude: number; cityLongitude: number };

function isLocated(card: BrowserListing): card is Located {
  return card.cityLatitude !== null && card.cityLongitude !== null;
}

function toClusterListings(cards: readonly BrowserListing[]): ClusterListing[] {
  const byId = (left: Located, right: Located) => left.id.localeCompare(right.id);
  return [...cards.filter(isLocated)].sort(byId).map((card) => ({
    id: card.id,
    title: card.card.name,
    productTypeSlug: card.card.productTypeSlug,
    buyerUnitPriceCents: card.buyerUnitPriceCents,
    cityName: card.cityName,
    stateAbbr: card.stateAbbr,
    cityLatitude: card.cityLatitude,
    cityLongitude: card.cityLongitude,
    primaryImageUrl: card.card.imageUrl,
    href: card.card.href,
  }));
}

function ResultsBody({
  listing,
  highlightedId,
  onHoverCard,
}: {
  listing: ListingResults;
  highlightedId: string | null;
  onHoverCard: (id: string | null) => void;
}) {
  if (listing.failed) {
    return (
      <EmptyState
        icon={RotateCcw}
        title="Listings could not load"
        description="Check your connection and try again."
        action={{ label: "Try again", icon: RotateCcw, onClick: listing.requestAll }}
      />
    );
  }
  if (listing.resultCount === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title="No listings match"
        action={{
          label: "Clear search and filters",
          icon: RotateCcw,
          onClick: () => listing.setFilters(clearSearchAndFilters(listing.filters)),
        }}
      />
    );
  }
  return (
    <div aria-busy={listing.isLoading || undefined} className={listing.isLoading ? "opacity-60" : undefined}>
      <ListingResultsGrid
        cards={listing.pageCards}
        firstIsLcp={listing.currentPage === 1 && !listing.isLoading}
        highlightedId={highlightedId}
        onHoverCard={onHoverCard}
      />
    </div>
  );
}

function LocationClusterMap({
  cards,
  mapListings,
  mapView,
  highlight,
  onHighlight,
  viewport,
}: {
  cards: readonly BrowserListing[];
  mapListings: ClusterListing[];
  mapView: MapView;
  highlight: Highlight;
  onHighlight: (highlight: Highlight) => void;
  viewport: "desktop" | "mobile";
}) {
  const firstLocated = cards.find(isLocated);
  return (
    <ListingsClusterMapClient
      listings={mapListings}
      focalCenter={mapView.focalCenter}
      defaultCenter={
        firstLocated
          ? { latitude: firstLocated.cityLatitude, longitude: firstLocated.cityLongitude }
          : undefined
      }
      defaultZoom={mapView.defaultZoom}
      className="h-full w-full"
      mapClassName="h-full w-full rounded-none border-0"
      highlightedId={highlight?.id ?? null}
      onHoverListing={(id) => onHighlight(id ? { id, source: "map" } : null)}
      viewport={viewport}
    />
  );
}

function useListingBrowser(source: LocationListingSource, serverHasMap: boolean) {
  const listing = useListingResults(source);
  const hasMap = listing.all ? listing.all.some(isLocated) : serverHasMap;
  const [highlight, setHighlight] = useState<Highlight>(null);
  const paneRef = useRef<HTMLElement>(null);
  const mapListings = useMemo(() => toClusterListings(listing.pageCards), [listing.pageCards]);
  const changePage = (page: number) => {
    listing.setPage(page);
    scrollCatalogueToTop(paneRef.current);
  };
  const changeFilters = (filters: Parameters<typeof listing.setFilters>[0]) => {
    listing.setFilters(filters);
    scrollCatalogueToTop(paneRef.current);
  };
  return { listing, hasMap, highlight, setHighlight, paneRef, mapListings, changePage, changeFilters };
}

function PrefetchOnIntent({ onIntent, children }: { onIntent: () => void; children: ReactNode }) {
  return (
    <div onPointerEnter={onIntent} onFocusCapture={onIntent} onTouchStart={onIntent}>
      {children}
    </div>
  );
}

function BrowserFilterRow({
  browser,
  hasDistance,
}: {
  browser: ReturnType<typeof useListingBrowser>;
  hasDistance: boolean;
}) {
  const { listing, changeFilters } = browser;
  return (
    <PrefetchOnIntent onIntent={listing.requestAll}>
      <div className="@container">
        <div className="mx-auto flex max-w-[100rem] px-4 pt-3 pb-4 @3xl:px-8">
          <ListingBrowserControls
            cards={listing.all}
            listingsFailed={listing.listingsFailed}
            options={listing.options}
            filters={listing.filters}
            hasDistance={hasDistance}
            onFiltersChange={changeFilters}
            onClearAll={() => changeFilters(clearSearchAndFilters(listing.filters))}
          />
        </div>
      </div>
    </PrefetchOnIntent>
  );
}

function BrowserResults({
  browser,
  mapView,
  quoteProductSlug,
}: {
  browser: ReturnType<typeof useListingBrowser>;
  mapView: MapView;
  quoteProductSlug: string;
}) {
  const { listing, hasMap, highlight, setHighlight, mapListings, changePage } = browser;
  return (
    <>
      <ResultsBody
        listing={listing}
        highlightedId={highlight?.source === "map" ? highlight.id : null}
        onHoverCard={(id) => setHighlight(id ? { id, source: "card" } : null)}
      />
      <PrefetchOnIntent onIntent={listing.requestAll}>
        <ListingPagination currentPage={listing.currentPage} pageCount={listing.pageCount} onPageChange={changePage} />
      </PrefetchOnIntent>
      <CatalogueQuoteCard productSlug={quoteProductSlug} />
      {hasMap ? (
        <div className="mt-8">
          <CatalogueMobileMap overlayTitle={mapView.mobileTitle}>
            <LocationClusterMap
              cards={listing.pageCards}
              mapListings={mapListings}
              mapView={mapView}
              highlight={highlight}
              onHighlight={setHighlight}
              viewport="mobile"
            />
          </CatalogueMobileMap>
        </div>
      ) : null}
    </>
  );
}

function LocationCatalogue({
  hasMap: serverHasMap,
  hasDistance,
  mapView,
  title,
  summary,
  breadcrumbs,
  quoteProductSlug,
  seo,
  ...source
}: Omit<LocationListingBrowserProps, "productName" | "locationName" | "emptyMessage">) {
  const browser = useListingBrowser(source, serverHasMap);
  const { listing, hasMap, highlight, setHighlight, mapListings } = browser;
  return (
    <CatalogueShell
      header={<CatalogueHeaderBar breadcrumbs={breadcrumbs} />}
      heading={
        <CatalogueResultsHeading
          title={title}
          subtitle={`${listingCountLabel(listing.resultCount, listing.total)} · ${summary}`}
        />
      }
      filterBar={<BrowserFilterRow browser={browser} hasDistance={hasDistance} />}
      mapColumn={
        hasMap ? (
          <CatalogueMapColumn>
            <LocationClusterMap
              cards={listing.pageCards}
              mapListings={mapListings}
              mapView={mapView}
              highlight={highlight}
              onHighlight={setHighlight}
              viewport="desktop"
            />
          </CatalogueMapColumn>
        ) : null
      }
      paneRef={browser.paneRef}
      after={seo}
    >
      <BrowserResults browser={browser} mapView={mapView} quoteProductSlug={quoteProductSlug} />
    </CatalogueShell>
  );
}

export function LocationListingBrowser({
  productName,
  locationName,
  emptyMessage,
  ...browser
}: LocationListingBrowserProps) {
  if (browser.total > 0) return <LocationCatalogue {...browser} />;
  return (
    <CatalogueShell
      header={<CatalogueHeaderBar breadcrumbs={browser.breadcrumbs} />}
      after={browser.seo}
    >
      <CatalogueResultsHeading title={browser.title} subtitle={browser.summary} />
      <div className="p-4">
        <ListingEmptyState emptyMessage={emptyMessage} locationName={locationName} productName={productName} />
      </div>
    </CatalogueShell>
  );
}
